import { useState, useCallback, useEffect } from "react";
import { memoryStore } from "../utils/storage";
import { useAuth } from "../contexts/AuthContext";

// ────────────────────────────────────────────────────────────────────────────
// Import-based store for Clal Apex data (health + life). Each import = one
// .exe file, carrying parsed members (policy-level fund objects). Mirrors the
// Hachshara/Harel import pattern so App.jsx can attach funds identically.
// ────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "pension_clal_v2";

function readStore() {
  try {
    const raw = memoryStore.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useClalHealthStore() {
  const { syncVersion } = useAuth();
  const [imports, setImports] = useState(readStore);

  useEffect(() => {
    const onUpdate = () => setImports(readStore());
    window.addEventListener("pension-data-synced", onUpdate);
    window.addEventListener("pension-clal-updated", onUpdate);
    return () => {
      window.removeEventListener("pension-data-synced", onUpdate);
      window.removeEventListener("pension-clal-updated", onUpdate);
    };
  }, []);

  useEffect(() => {
    if (syncVersion > 0) setImports(readStore());
  }, [syncVersion]);

  const persist = useCallback((next) => {
    try {
      memoryStore.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event("pension-clal-updated"));
    } catch (e) {
      console.error("Failed to save Clal imports to memoryStore", e);
    }
  }, []);

  const addClalImport = useCallback(
    (members, fileMeta = {}) => {
      if (!members?.length) return { membersAdded: 0 };
      const prev = readStore();
      const kind = fileMeta.kind || "health";
      const reportDate = fileMeta.taarichBitzua || null;
      // period = the value month (YYYY-MM). Each monthly Clal file is a FULL
      // snapshot of that product type, so we keep ONE import per (kind, period)
      // — preserving monthly history like the holdings products — and replace
      // only a re-import of the SAME month. The dashboard renders the latest
      // period per kind (see App.jsx), so older months are kept but don't
      // create duplicate cards.
      const period = (reportDate || new Date().toISOString()).slice(0, 7);
      const next = prev.filter(
        (imp) => !(
          (imp.kind || "health") === kind &&
          (imp.period || (imp.reportDate || imp.addedAt || "").slice(0, 7)) === period &&
          imp.fileName === (fileMeta.fileName || "")
        )
      );
      next.push({
        id: `clal_${kind}_${period}`,
        fileName: fileMeta.fileName || "",
        kind,
        period,
        reportDate,
        companyCode: "78",
        sourceType: fileMeta.sourceType,
        members,
        addedAt: new Date().toISOString(),
      });
      persist(next);
      setImports(next);
      return { membersAdded: members.length };
    },
    [persist]
  );

  const removeClalImport = useCallback(
    (fileName) => {
      const next = readStore().filter((imp) => imp.fileName !== fileName);
      persist(next);
      setImports(next);
    },
    [persist]
  );

  const removeClalClient = useCallback(
    (taz) => {
      const prev = readStore();
      const next = prev
        .map((imp) => ({ ...imp, members: imp.members.filter((m) => m.taz !== taz) }))
        .filter((imp) => imp.members.length > 0);
      persist(next);
      setImports(next);
    },
    [persist]
  );

  return { clalImports: imports, addClalImport, removeClalImport, removeClalClient };
}
